import React, { useEffect } from 'react';
import mustache from 'mustache';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import Panel from '../../ui/Panel/Panel';
import { MarketingAsset } from '../../../../../__generated__/loma';
import styles from './Asset.module.css';
import { useTheme } from '../../../hooks/useTheme';

export interface AssetProps {
  assetId: string;
  asset: Pick<
    MarketingAsset,
    'category' | 'destinationUrl' | 'altText' | 'cdnUrl'
  >;
  featureName?: string;
  fixedPanelHeight?: string;
  templateRenderingContext?: unknown;
  hasBorder?: boolean;
}

const Asset = ({
  featureName,
  assetId,
  asset,
  fixedPanelHeight,
  templateRenderingContext,
  hasBorder,
}: AssetProps) => {
  const {
    imgPrimaryNone: imgStyle,
    assetpanelcontainerNoneNone: containerStyle,
    panelcontainerNoneNone: panelContainerStyle,
    panelcontainerNoneNone: {
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomLeftRadius,
      borderBottomRightRadius,
    } = {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  } = useTheme();

  const logEvent = useAnalyticsPost();
  const baseLogInfo = {
    feature: featureName,
    pageComponent: NarvarEventComponents.NTH_BLOCK_ASSET,
    eventComponent: NarvarEventComponents.NTH_BLOCK_ASSET,
    eventFeature: NarvarEventComponents.NTH_BLOCK_ASSET,
  };

  useEffect(
    () => {
      if (!assetId || !asset) return;

      const { category, altText, cdnUrl, name }: MarketingAsset = asset;

      logEvent({
        ...baseLogInfo,
        clickAssetId: assetId,
        clickAssetType: category,
        clickAssetUrl: cdnUrl || '',
        clickLinkLabel: name || altText,
        eventType: NarvarEventTypes.COMPONENT_RENDER,
      });
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetId],
  );

  if (!asset) return null;
  const { category, destinationUrl, altText, cdnUrl }: MarketingAsset = asset;

  if (!cdnUrl) return null;

  const renderedDestinationUrl = mustache.render(
    destinationUrl || '',
    templateRenderingContext,
  );

  const img = fixedPanelHeight ? (
    <div
      role="img"
      aria-label={altText || ''}
      className={styles.container}
      style={{
        backgroundImage: `url(${cdnUrl})`,
        borderTopLeftRadius,
        borderTopRightRadius,
        borderBottomLeftRadius,
        borderBottomRightRadius,
        ...imgStyle,
      }}
    />
  ) : (
    <img
      src={cdnUrl}
      alt={altText || ''}
      data-testid="blockAssetImage"
      className={styles.image}
      style={{
        borderTopLeftRadius,
        borderTopRightRadius,
        borderBottomLeftRadius,
        borderBottomRightRadius,
        ...imgStyle,
      }}
    />
  );

  return (
    <Panel
      sOuter={{
        ...containerStyle,
        ...(hasBorder && {
          ...panelContainerStyle,
        }),
        overflowY: 'visible',
        boxSizing: 'border-box',
        height:
          fixedPanelHeight ||
          (containerStyle && containerStyle.height) ||
          '100%',
      }}
      sInner={{
        overflowY: 'visible',
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
      }}
      header
    >
      {destinationUrl ? (
        <a
          className={styles.link}
          href={renderedDestinationUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            borderTopLeftRadius,
            borderTopRightRadius,
            borderBottomLeftRadius,
            borderBottomRightRadius,
          }}
          onClick={(): void =>
            logEvent({
              ...baseLogInfo,
              clickAssetId: assetId,
              clickAssetType: category,
              clickAssetUrl: cdnUrl,
              clickCategory: NarvarClickSubTypes.MARKETING_ASSET,
              clickLinkDestinationUrl: renderedDestinationUrl,
              clickLinkLabel: altText,
              clickSubType: NarvarClickSubTypes.MARKETING_ASSET,
              clickType: NarvarClickTypes.OUTGOING,
              eventType: NarvarEventTypes.CLICK,
            })
          }
        >
          {img}
        </a>
      ) : (
        img
      )}
    </Panel>
  );
};

export default Asset;
